import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    Input,
} from '@angular/core';

import { ProgressBarData, ThresholdData } from '../metric-performance.metadata';

import { AdjustedSide, FlagData, FlagSide, FlagType } from './flags.metadata';

@Component({
    selector: 'cmps-metric-progress-bar',
    template: `
        <div class="cmps-metric-progress-bar--row">
            <span class="cmps-metric-progress-bar--range-label">{{
                progressBarData.minValue
            }}</span>
            <div class="cmps-metric-progress-bar--column">
                <div class="cmps-metric-progress-bar--background-bar"></div>
                <div class="cmps-metric-progress-bar--progress-bar">
                    <ng-container
                        *ngFor="
                            let threshold of progressBarData.thresholds;
                            let isFirst = first;
                            let isLast = last
                        "
                    >
                        <div
                            class="cmps-metric-progress-bar--progress-bar-section"
                            [style.width.%]="getThresholdWidth(threshold)"
                            [style.background]="threshold.colorGradient"
                            [class.first-section]="isFirst"
                            [class.last-section]="isLast"
                            [class.active-segment]="threshold.enabled"
                        ></div>
                    </ng-container>

                    <!-- Actual Flag (above the bar) -->
                    <div
                        [class]="
                            'cmps-metric-progress-bar--flag cmps-metric-progress-bar--actual-flag flag-above' +
                            getAdjustedClass(flagType.ACTUAL)
                        "
                        [style.left.%]="actualFlagPosition"
                        [cmpsTooltipTrigger]="
                            progressBarData.actualValue.tooltipText
                        "
                    >
                        <cmps-icon
                            svgIcon="__internal:account-box"
                            class="actual-icon"
                            style="fill: #1e60d4;"
                        ></cmps-icon>
                        <cmps-flag
                            [flagClass]="getFlagClass(flagType.ACTUAL)"
                            [isOverlapping]="isOverlapping(flagType.ACTUAL)"
                        ></cmps-flag>
                    </div>

                    <!-- SuperGroup Flag (above the bar) -->
                    <div
                        *ngIf="progressBarData.superGroupValue"
                        [class]="
                            'cmps-metric-progress-bar--flag cmps-metric-progress-bar--supergroup-flag flag-above' +
                            getAdjustedClass(flagType.SUPERGROUP)
                        "
                        [style.left.%]="superGroupFlagPosition"
                        [cmpsTooltipTrigger]="
                            progressBarData.superGroupValue?.tooltipText
                        "
                    >
                        <cmps-icon
                            svgIcon="__internal:account-group"
                            class="supergroup-icon"
                            style="fill: #000;"
                        ></cmps-icon>
                        <cmps-flag
                            [flagClass]="getFlagClass(flagType.SUPERGROUP)"
                            [isOverlapping]="isOverlapping(flagType.SUPERGROUP)"
                        ></cmps-flag>
                    </div>

                    <!-- Target Flag (below the bar) -->
                    <div
                        [class]="
                            'cmps-metric-progress-bar--flag cmps-metric-progress-bar--target-flag flag-below' +
                            getAdjustedClass(flagType.TARGET)
                        "
                        [style.left.%]="targetFlagPosition"
                        [cmpsTooltipTrigger]="
                            progressBarData.targetValue.tooltipText
                        "
                    >
                        <cmps-icon
                            svgIcon="__internal:target"
                            class="target-icon"
                            style="fill: #505050;"
                        ></cmps-icon>
                        <cmps-flag
                            [flagClass]="getFlagClass(flagType.TARGET)"
                            [isOverlapping]="isOverlapping(flagType.TARGET)"
                        ></cmps-flag>
                    </div>

                    <!-- Goal Flag (below the bar) -->
                    <div
                        *ngIf="progressBarData.goalValue"
                        [class]="
                            'cmps-metric-progress-bar--flag cmps-metric-progress-bar--goal-flag flag-below' +
                            getAdjustedClass(flagType.GOAL)
                        "
                        [style.left.%]="goalFlagPosition"
                        [cmpsTooltipTrigger]="
                            progressBarData.goalValue?.tooltipText
                        "
                    >
                        <cmps-icon
                            svgIcon="__internal:flag-variant"
                            class="goal-icon"
                            style="fill: #505050;"
                        ></cmps-icon>
                        <cmps-flag
                            [flagClass]="getFlagClass(flagType.GOAL)"
                            [isOverlapping]="isOverlapping(flagType.GOAL)"
                        ></cmps-flag>
                    </div>
                </div>
            </div>
            <span class="cmps-metric-progress-bar--range-label">{{
                progressBarData.maxValue
            }}</span>
        </div>
    `,
})
export class CmpsMetricProgressBarComponent implements AfterViewInit {
    @Input() progressBarData: ProgressBarData;

    actualFlagPosition: number;
    superGroupFlagPosition: number;
    targetFlagPosition: number;
    goalFlagPosition: number;
    flagType = FlagType;
    flags: FlagData[] = [
        {
            name: FlagType.ACTUAL,
            side: FlagSide.ABOVE,
            position: 0,
            adjusted: false,
            nearEdge: false,
            adjustedSide: AdjustedSide.NONE,
        },
        {
            name: FlagType.SUPERGROUP,
            side: FlagSide.ABOVE,
            position: 0,
            adjusted: false,
            nearEdge: false,
            adjustedSide: AdjustedSide.NONE,
        },
        {
            name: FlagType.TARGET,
            side: FlagSide.BELOW,
            position: 0,
            adjusted: false,
            nearEdge: false,
            adjustedSide: AdjustedSide.NONE,
        },
        {
            name: FlagType.GOAL,
            side: FlagSide.BELOW,
            position: 0,
            adjusted: false,
            nearEdge: false,
            adjustedSide: AdjustedSide.NONE,
        },
    ];

    constructor(private readonly cdr: ChangeDetectorRef) {}

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.adjustFlagPositions();
        });
    }
    /* ------------------------- Positioning Methods ------------------------- */

    adjustFlagPositions(): void {
        this.actualFlagPosition = this.calculateFlagPosition(
            this.progressBarData.actualValue.value,
        );
        this.superGroupFlagPosition = this.progressBarData.superGroupValue
            ? this.calculateFlagPosition(
                  this.progressBarData.superGroupValue.value,
              )
            : 0;
        this.targetFlagPosition = this.calculateFlagPosition(
            this.progressBarData.targetValue.value,
        );
        this.goalFlagPosition = this.progressBarData.goalValue
            ? this.calculateFlagPosition(this.progressBarData.goalValue.value)
            : 0;
        this.setFlagPositions();
        this.detectOverlaps();
        this.cdr.detectChanges();
    }

    setFlagPositions(): void {
        const map = new Map<FlagType, number>([
            [FlagType.ACTUAL, this.actualFlagPosition],
            [FlagType.SUPERGROUP, this.superGroupFlagPosition],
            [FlagType.TARGET, this.targetFlagPosition],
            [FlagType.GOAL, this.goalFlagPosition],
        ]);

        this.flags.forEach((flag) => {
            const pos = map.get(flag.name);
            if (pos !== undefined) {
                flag.position = pos;
            }
        });
    }

    detectOverlaps(): void {
        const threshold = 6;
        const edgeThreshold = 95;
        const above = this.flags.filter((f) => f.side === FlagSide.ABOVE);
        const below = this.flags.filter((f) => f.side === FlagSide.BELOW);
        this.checkPair(above, threshold, edgeThreshold);
        this.checkPair(below, threshold, edgeThreshold);
    }

    checkPair(
        group: FlagData[],
        threshold: number,
        edgeThreshold: number,
    ): void {
        if (group.length < 2) return;
        const [f1, f2] = group;
        const distance = Math.abs(
            this.getValue(f1.name) - this.getValue(f2.name),
        );
        const isHigherToLow =
            this.progressBarData.maxValue < this.progressBarData.minValue;
        const percentageDifference =
            (distance /
                Math.max(this.getValue(f1.name), this.getValue(f2.name))) *
            100;
        if (
            (!isHigherToLow && distance <= threshold) ||
            (isHigherToLow && percentageDifference <= threshold)
        ) {
            if (f1.position > f2.position)
                this.updateFlagAdjustment(f1, f2, edgeThreshold);
            else this.updateFlagAdjustment(f2, f1, edgeThreshold);
        }
    }

    updateFlagAdjustment(
        topFlag: FlagData,
        bottomFlag: FlagData,
        edgeThreshold: number,
    ): void {
        if (topFlag.position > edgeThreshold) {
            bottomFlag.adjusted = true;
            bottomFlag.nearEdge = bottomFlag.position > edgeThreshold;
            bottomFlag.adjustedSide = AdjustedSide.RIGHT;
            topFlag.adjustedSide = AdjustedSide.NONE;
        } else if (bottomFlag.position > edgeThreshold) {
            bottomFlag.adjusted = true;
            bottomFlag.nearEdge = true;
            bottomFlag.adjustedSide = AdjustedSide.RIGHT;
            topFlag.adjustedSide = AdjustedSide.NONE;
        } else {
            topFlag.adjusted = true;
            topFlag.adjustedSide = AdjustedSide.LEFT;
            bottomFlag.adjustedSide = AdjustedSide.NONE;
        }
    }

    getValue(type: FlagType): number {
        if (type === FlagType.ACTUAL)
            return this.progressBarData.actualValue?.value;
        if (type === FlagType.SUPERGROUP)
            return this.progressBarData.superGroupValue?.value;
        if (type === FlagType.TARGET)
            return this.progressBarData.targetValue?.value;
        if (type === FlagType.GOAL)
            return this.progressBarData.goalValue?.value;
        return 0;
    }

    /* ------------------------- Calculation Helpers ------------------------- */
    calculateFlagPosition(value: number): number {
        const minValue = Number(this.progressBarData.minValue);
        const maxValue = Number(this.progressBarData.maxValue);

        let clampedValue = value;
        if (minValue < maxValue) {
            clampedValue = Math.max(minValue, Math.min(value, maxValue));
        } else {
            clampedValue = Math.max(maxValue, Math.min(value, minValue));
        }

        if (minValue < maxValue) {
            return ((clampedValue - minValue) / (maxValue - minValue)) * 100;
        } else {
            return ((minValue - clampedValue) / (minValue - maxValue)) * 100;
        }
    }

    /* ------------------------- Overlap & Transform Logic ------------------------- */
    isOverlapping(name: FlagType): boolean {
        const f = this.flags.find((x) => x.name === name);
        return !!f?.adjusted;
    }

    getFlagClass(name: FlagType): string {
        const f = this.flags.find((x) => x.name === name);
        let base = `cmps-metric-progress-bar--${FlagType[name]
            .toString()
            .toLowerCase()}-flag-line`;
        if (f?.side === FlagSide.ABOVE) base += ' flag-line-above';
        if (f?.side === FlagSide.BELOW) base += ' flag-line-below';

        if (f?.adjustedSide === AdjustedSide.LEFT) base += ' overlap-left';
        if (f?.adjustedSide === AdjustedSide.RIGHT) base += ' overlap-right';
        return base;
    }

    getAdjustedClass(flagType: FlagType): string {
        const flag = this.flags.find((f) => f.name === flagType);
        const flagName = FlagType[flagType].toString().toLowerCase();
        let classes = '';

        if (flag?.adjustedSide === AdjustedSide.LEFT) {
            classes += ` cmps-metric-progress-bar--${flagName}-flag-overlap-left`;
        } else if (flag?.adjustedSide === AdjustedSide.RIGHT) {
            classes += ` cmps-metric-progress-bar--${flagName}-flag-overlap-right`;
        }

        if (flag?.position === 100) {
            classes += ' cmps-metric-progress-bar-flag-at-max-value';
        }

        return classes;
    }

    getThresholdWidth(threshold: ThresholdData): number {
        const totalRange =
            this.progressBarData.maxValue - this.progressBarData.minValue;
        const thresholdRange = threshold.endValue - threshold.startValue;
        const width = (thresholdRange / totalRange) * 100;
        return Math.abs(width);
    }
}
